import { ActionButton } from 'Components/common/ActionButton/ActionButton'
import styles from './ExamInformation.module.less'
import { useNavigate, useParams } from 'react-router-dom'
import { secondsToMinutes } from 'Utils/common.utils'
import { NO_TIME_LIMIT_LABEL } from 'Constants/constants'
import { ExamAttempt } from 'Types/Exams.types'
import { notification } from 'antd/lib'
import { useCreateAttempt } from 'Hooks/exams/use-create-attempt'
import { useCallback, useEffect } from 'react'

type ExamInformationProps = {
    questions: number
    duration: number | null
    attempts: number
    attempt: ExamAttempt | null
    isInProgress: boolean
    isArchived: boolean
    timeLeftInSecs?: number
}

export const ExamInformation: React.FC<ExamInformationProps> = ({
    questions,
    duration,
    attempts,
    isInProgress,
    isArchived,
    attempt,
    timeLeftInSecs,
}) => {
    const navigate = useNavigate()
    const params = useParams()
    const { createAttempt, error } = useCreateAttempt()

    const isTimeExpired = duration && !timeLeftInSecs
    const canResumeExam = isInProgress && !isTimeExpired
    const canStartExam = attempts > 0

    const handleResumeExam = useCallback(() => {
        const attemptId = attempt?.id
        if (!canResumeExam || !attemptId) {
            return
        }
        navigate(`/exam/${params.examId}/attempt/${attemptId}`)
    }, [attempt?.id, canResumeExam, navigate, params.examId])

    const handleStartExam = useCallback(async () => {
        if (!canStartExam) {
            return
        }
        const response = await createAttempt()
        if (response) {
            navigate(`/exam/${params.examId}/attempt/${response.attempt_id}`)
        }
    }, [canStartExam, createAttempt, navigate, params.examId])

    useEffect(() => {
        if (error) {
            // Extract error message from the error object
            const errorMessage = (error as Error)?.message || 'An error occurred'

            notification.error({
                message: 'Failed to start exam',
                description: errorMessage,
                placement: 'bottomLeft',
            })
        }
    }, [error])

    return (
        <div className={styles['exam-information']} data-testid="exam-information-wrapper">
            <h3 className={styles['exam-information-title']}>Exam Details</h3>
            <p className={styles['exam-information-text']}>
                No of questions: <b>{questions}</b>
            </p>
            <p className={styles['exam-information-text']}>
                Total time limit:{' '}
                <b>{duration ? secondsToMinutes(duration) + ' mins' : NO_TIME_LIMIT_LABEL}</b>
            </p>
            <p className={styles['exam-information-text']}>
                Attempts Remaining: <b>{attempts}</b>
            </p>
            <div className={styles['exam-information-button']}>
                <ActionButton
                    disabled={(!canResumeExam && !canStartExam) || isArchived}
                    title={canResumeExam ? 'Resume Exam' : 'Start Exam'}
                    onClick={canResumeExam ? handleResumeExam : handleStartExam}
                />
            </div>
        </div>
    )
}
